import { type CSSProperties, type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from './Button';
import styles from './FilterPopover.module.css';

interface Props {
  /** How many filters differ from the defaults (0 = none: no indicator). */
  activeCount: number;
  /** Resets every filter of the popover to its default. */
  onReset: () => void;
  /** Accessible name of the popover; defaults to "Filters". */
  title?: string;
  children: ReactNode;
}

/**
 * One "Filters" button with a compact popover. The button shows how many filters are active.
 * Escape and a click outside close it (focus returns to the button); every control inside is a
 * normal form control, so it is fully keyboard operable. Filtering only hides items in a view.
 */
export function FilterPopover({ activeCount, onReset, title, children }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const [placement, setPlacement] = useState<CSSProperties>({});
  const name = title ?? t('filters.title');

  // The panel is `position: fixed` and placed from the button's rectangle: it floats over the
  // page, never takes part in any scroll container's size, and always stays in the viewport
  // (below the button when it fits, else above, with its own scrolling).
  useEffect(() => {
    if (!open) return;
    const place = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      const margin = 8;
      const width = Math.min(352, window.innerWidth - 2 * margin);
      const below = window.innerHeight - rect.bottom - margin;
      const above = rect.top - margin;
      const useAbove = below < 240 && above > below;
      const room = Math.max(120, useAbove ? above : below);
      setPlacement({
        left: Math.max(margin, Math.min(rect.left, window.innerWidth - width - margin)),
        width,
        maxHeight: room,
        ...(useAbove ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && rootRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [open]);

  return (
    <div
      ref={rootRef}
      className={styles.root}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.preventDefault();
          event.stopPropagation(); // not the drawer / dialog behind
          setOpen(false);
          buttonRef.current?.focus();
        }
      }}
    >
      <Button
        ref={buttonRef}
        size="sm"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        data-active={activeCount > 0 ? 'true' : undefined}
        className={activeCount > 0 ? styles.active : undefined}
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        {t('filters.title')}
        {activeCount > 0 && (
          <span className={styles.badge} aria-label={t('filters.active', { count: activeCount })}>
            {activeCount}
          </span>
        )}
      </Button>
      {open && (
        <div
          id={panelId}
          className={styles.panel}
          role="dialog"
          aria-label={name}
          style={placement}
        >
          {children}
          <div className={styles.footer}>
            <Button size="sm" variant="ghost" disabled={activeCount === 0} onClick={onReset}>
              {t('filters.reset')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
